///
/// EarthBrowser
///
/// Created by Matt Giger on 9/13/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The private full-access offer surface: long-tenured signed-in free
/// accounts (eligibility decided server-side) get a quiet, premium-framed
/// card offering 30 days of full access — recognition, not a discount, and
/// never rendered to anyone else, so the public price image never moves.
/// "Not now" snoozes it for two weeks (persisted client-side); accepting
/// grants the subscription immediately, no card required.
///

import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/use-toast'
import { snoozeFullAccessOffer } from '@/redux/engagementSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import {
	useAcceptFullAccessOfferMutation,
	useGetFullAccessOfferQuery,
	useGetUserProfileQuery,
} from '@/redux/userApi'
import { track } from '@/utils/analytics'
import { Sparkles } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { slotPriority, useNotificationSlot } from './NotificationSlot'

export function FullAccessOfferCard() {
	const dispatch = useAppDispatch()
	const { data: userProfile } = useGetUserProfileQuery()
	const { data: offer } = useGetFullAccessOfferQuery(undefined, { skip: !userProfile })
	const [ acceptOffer, { isLoading: isAccepting } ] = useAcceptFullAccessOfferMutation()
	const snoozedUntil = useAppSelector(state => state.engagement.offerSnoozedUntil)

	const snoozed = snoozedUntil !== null && new Date(snoozedUntil) > new Date()
	const wants = !!userProfile && offer?.eligible === true && !snoozed
	const visible = useNotificationSlot('full-access-offer', wants, slotPriority.fullAccessOffer)

	// Funnel event: emitted once per appearance, not per re-render
	const capturedShown = useRef(false)
	useEffect(() => {
		if (!visible || capturedShown.current) return
		capturedShown.current = true
		track('full_access_offer_shown', {})
	}, [visible])

	if (!visible) return null

	const handleAccept = async () => {
		try {
			const result = await acceptOffer().unwrap()
			track('full_access_offer_accepted', {})
			const until = new Date(result.expiresAt).toLocaleDateString(undefined, {
				month: 'long', day: 'numeric',
			})
			toast({
				title: "Full access activated",
				description: `Every layer and feature is yours through ${until}. Enjoy!`,
				duration: 8000,
			})
		} catch {
			toast({
				title: "Something went wrong",
				variant: "destructive",
				description: "We couldn't activate the offer. Please try again later.",
			})
		}
	}

	const handleSnooze = () => {
		track('full_access_offer_snoozed', {})
		dispatch(snoozeFullAccessOffer())
	}

	return (
		<div className="w-[min(26rem,calc(100vw-2rem))]">
			<div className="rounded-xl border border-white/20 bg-black/40 backdrop-blur-xl p-5 shadow-2xl animate-in fade-in slide-in-from-top-2 duration-500">
				<div className="flex items-start gap-3">
					<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/10">
						<Sparkles className="h-4 w-4 text-cyan-400" />
					</div>
					<div>
						<p className="text-sm font-semibold text-white">
							A month of full access, on us
						</p>
						<p className="mt-1 text-xs leading-relaxed text-white/70">
							You&apos;ve been exploring with us for a while. As thanks, enjoy
							{offer ? ` ${offer.grantDays}` : " 30"} days of every layer and
							feature — no card, nothing to cancel.
						</p>
					</div>
				</div>
				<div className="mt-4 flex items-center justify-end gap-2">
					<button
						onClick={handleSnooze}
						className="px-3 py-1.5 text-xs text-white/50 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-lg"
					>
						Not now
					</button>
					<Button
						size="sm"
						onClick={() => void handleAccept()}
						disabled={isAccepting}
						className="h-8 rounded-lg px-4 text-xs bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white transition-all duration-300 hover:scale-105"
					>
						{isAccepting ? "Activating…" : "Activate full access"}
					</Button>
				</div>
			</div>
		</div>
	)
}
